/**
 * Landing — marketing home ("Editorial Paper" direction).
 *
 * Ported 1:1 from the Claude Design source of truth: project "WYDM App"
 * → WYDM Landing.html. Markup, copy, inline styles and the two script
 * blocks all come straight from that file; Landing.css is its style
 * block verbatim.
 *
 * Positioning: the card spreads through your social circle until it
 * reaches your next date — "Your next date is a few friends away."
 *
 * Renders inside PublicShell, which supplies the sticky nav (wordmark +
 * For agents + Sign in + "Build my card") and the footer — both already
 * match the design's global chrome, so this file renders only the page
 * body.
 *
 * Sections (top → bottom): Hero (card + share wires/badges) · Spread
 * (social-graph animation) · How it works · Built to go viral ·
 * Discover nearby (map) · Message anyone (dark band) · Keepers.
 *
 * All landing-only styles live in Landing.css, scoped under `.lp`. The
 * faux-QR, stylized map and scroll-reveal motion are ported from the
 * design's first script into a single effect scoped to this component's
 * root and cleaned up on unmount; the #spread animation is the second
 * script, shared with /agents via spreadGraph.ts. Content renders fully
 * even with reduced motion / no reveal.
 *
 * Copy is the design's, verbatim; no fetches.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AppStoreBadge, PlayStoreBadge } from '@/components/StoreBadges';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/stores';
import { storePendingReferralCode } from '@/lib/referral';
import { storePendingAttribution } from '@/lib/attribution';
import { appDownloadHref } from '@/lib/appDownloadLink';
import { mountSpreadGraph, HUMAN_VARIANT } from './spreadGraph';
import { ShareWires, ShareBadges } from './shareBadges';
import './Landing.css';

const SIGNUP = '/auth/signup';

/* ── small inline icons (match the design's SVGs) ─────────── */
function Arrow({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Heart({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff">
      <path d="M12 21s-7.5-4.6-10-9.1C.4 8.8 1.6 5 5 5c2 0 3.2 1.2 4 2.4C9.8 6.2 11 5 13 5c3.4 0 4.6 3.8 3 6.9C18.5 16.4 12 21 12 21z" />
    </svg>
  );
}
function Lock({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="rgba(255,255,255,0.8)" strokeWidth={1.8} />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="rgba(255,255,255,0.8)" strokeWidth={1.8} />
    </svg>
  );
}
function Tick() {
  return (
    <span className="tick">
      <svg width={11} height={11} viewBox="0 0 24 24" fill="none">
        <path d="M5 12l5 5L19 7" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
function Send({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff">
      <path d="M3 11l18-8-8 18-2.4-7.6L3 11z" />
    </svg>
  );
}

/* ── reused: coral "Build my card" CTA → sign-up / builder ── */
function BuildMyCard({ style }: { style?: React.CSSProperties }) {
  return (
    <Link to={SIGNUP} className="btn btn-coral btn-lg" style={style}>
      Build my card <Arrow size={16} />
    </Link>
  );
}

/* ── reused: App Store + Google Play download badges ────────── */
/* Uses the shared StoreBadges components rather than the badge
 * components' own StoreBadgeRow: that helper's flex container has no
 * flex-wrap, and the two badges (~132px + ~148px + gap) overflow a
 * 320px viewport. `.lp .badges` already wraps. */
function StoreLinkBadges() {
  // When a creator code is pending, both badges route through /c/<code>
  // so the install can still be attributed; otherwise they keep pointing
  // straight at the stores exactly as before. Note this is the FALLBACK
  // path — signing up on this page is the deterministic one, which is
  // why the hero leads with signup rather than download.
  const href = appDownloadHref();
  const attributed = href !== undefined && href.includes('/c/');
  return (
    <div className="badges" style={{ marginTop: 24 }}>
      <AppStoreBadge href={attributed ? `${href}?go=store` : APP_STORE_URL} height={44} />
      <PlayStoreBadge href={attributed ? `${href}?go=store` : PLAY_STORE_URL} height={44} />
    </div>
  );
}

/* faux QR used inside the small step-card illustration */
function MiniQR() {
  return (
    <svg viewBox="0 0 24 24">
      <rect x="2" y="2" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" strokeWidth={2} />
      <rect x="4.5" y="4.5" width="2" height="2" fill="#1a1a1a" />
      <rect x="15" y="2" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" strokeWidth={2} />
      <rect x="17.5" y="4.5" width="2" height="2" fill="#1a1a1a" />
      <rect x="2" y="15" width="7" height="7" rx="1.5" fill="none" stroke="#1a1a1a" strokeWidth={2} />
      <rect x="4.5" y="17.5" width="2" height="2" fill="#1a1a1a" />
      <rect x="13" y="13" width="2" height="2" fill="#1a1a1a" />
      <rect x="18" y="16" width="2" height="2" fill="#1a1a1a" />
      <rect x="15" y="20" width="2" height="2" fill="#1a1a1a" />
    </svg>
  );
}

export function LandingPage() {
  const rootRef = useRef<HTMLDivElement>(null);

  // Creator/affiliate attribution capture (website-first funnel).
  //
  // This page is where a creator's link now LANDS — /c/<code> redirects
  // here with ?ref= preserved rather than bouncing to the store, because
  // attribution established at web signup is stamped on the account and
  // is exact on every platform, while a store-install match on iOS is
  // only probabilistic. Until this existed, wydm.app/?ref=CODE was
  // silently dropped and only /p/:slug and /join captured anything.
  //
  // Stashes into the SAME localStorage keys the signup screen already
  // drains (lib/referral.ts + lib/attribution.ts), so nothing downstream
  // changes. Runs once on mount; capture is best-effort by contract and
  // must never block the page.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) storePendingReferralCode(ref);
      // UTMs are captured independently of a code so a boosted post
      // without a creator code still reports a source.
      storePendingAttribution({
        utmSource: params.get('utm_source') ?? undefined,
        utmCampaign: params.get('utm_campaign') ?? undefined,
      });
    } catch {
      /* best-effort */
    }
  }, []);

  // Ports the design's first script: faux QR, stylized map, and the
  // IntersectionObserver scroll reveals. Scoped to this component's
  // root; all timers/observers cleaned up on unmount.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ns = 'http://www.w3.org/2000/svg';
    const timers: ReturnType<typeof setTimeout>[] = [];
    const observers: IntersectionObserver[] = [];

    /* faux QR — three finder squares + scattered modules, white tile */
    function buildQR(el: Element) {
      if (el.childElementCount > 0) return; // guard StrictMode double-run
      const svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 38 38');
      const dark = '#1a1a1a';
      const finder = (x: number, y: number) => {
        const g = document.createElementNS(ns, 'g');
        const o = document.createElementNS(ns, 'rect');
        o.setAttribute('x', String(x)); o.setAttribute('y', String(y));
        o.setAttribute('width', '11'); o.setAttribute('height', '11');
        o.setAttribute('rx', '2'); o.setAttribute('fill', 'none');
        o.setAttribute('stroke', dark); o.setAttribute('stroke-width', '2.2');
        const i = document.createElementNS(ns, 'rect');
        i.setAttribute('x', String(x + 4)); i.setAttribute('y', String(y + 4));
        i.setAttribute('width', '3'); i.setAttribute('height', '3'); i.setAttribute('fill', dark);
        g.appendChild(o); g.appendChild(i); return g;
      };
      svg.appendChild(finder(1, 1)); svg.appendChild(finder(26, 1)); svg.appendChild(finder(1, 26));
      ([[18,4],[22,8],[16,12],[26,14],[20,18],[30,6],[12,20],[24,24],[28,28],[18,30],[32,22],[22,32],[14,28],[30,32],[16,4],[26,30]] as const).forEach((m) => {
        const r = document.createElementNS(ns, 'rect');
        r.setAttribute('x', String(m[0])); r.setAttribute('y', String(m[1]));
        r.setAttribute('width', '2.6'); r.setAttribute('height', '2.6'); r.setAttribute('fill', dark);
        svg.appendChild(r);
      });
      el.appendChild(svg);
    }
    root.querySelectorAll('.qr').forEach(buildQR);

    /* stylized warm-toned map + profile-card pins */
    const miniQR = () => `<svg viewBox="0 0 24 24" width="100%" height="100%">
      <rect x="1" y="1" width="7" height="7" rx="1.4" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="3.5" y="3.5" width="2" height="2" fill="#1a1a1a"/>
      <rect x="16" y="1" width="7" height="7" rx="1.4" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="18.5" y="3.5" width="2" height="2" fill="#1a1a1a"/>
      <rect x="1" y="16" width="7" height="7" rx="1.4" fill="none" stroke="#1a1a1a" stroke-width="2"/><rect x="3.5" y="18.5" width="2" height="2" fill="#1a1a1a"/>
      <rect x="13" y="12" width="2" height="2" fill="#1a1a1a"/><rect x="18" y="14" width="2" height="2" fill="#1a1a1a"/><rect x="20" y="19" width="2" height="2" fill="#1a1a1a"/></svg>`;
    const mapSVG = () => `<svg viewBox="0 0 700 420" preserveAspectRatio="xMidYMid slice" style="position:absolute;inset:0;width:100%;height:100%;">
      <rect width="700" height="420" fill="#EFE3D2"/>
      <ellipse cx="120" cy="90" rx="120" ry="64" fill="#CFDCC2"/>
      <ellipse cx="560" cy="330" rx="110" ry="80" fill="#CFDCC2"/>
      <ellipse cx="260" cy="370" rx="70" ry="50" fill="#CFDCC2"/>
      <path d="M-20 180 C 200 150, 280 280, 460 270 S 720 220, 760 250" stroke="#BAD2D6" stroke-width="30" fill="none" stroke-linecap="round" opacity="0.9"/>
      <g stroke="#EAD3B0" stroke-width="6" stroke-linecap="round" fill="none" opacity="0.9">
        <path d="M-10 70 L 710 110"/><path d="M-10 250 L 710 300"/>
        <path d="M120 -10 L 170 430"/><path d="M380 -10 L 430 430"/><path d="M580 -10 L 620 430"/>
      </g>
      <g stroke="#E6C8A6" stroke-width="10" stroke-linecap="round" fill="none">
        <path d="M-10 40 C 200 110, 460 50, 720 120"/>
        <path d="M260 -10 C 220 160, 320 300, 280 430"/>
      </g>
      <text x="350" y="220" text-anchor="middle" fill="rgba(43,29,20,0.10)" style="font-family:'Fraunces',serif;font-style:italic;font-weight:600;font-size:64px;">nearby</text>
    </svg>`;
    const pin = (name: string, grad: number, x: number, y: number, z: number, delay: number) => `<div class="pin" style="left:${x}%;top:${y}%;z-index:${z};">
      <div class="pc photo-gradient-${grad}" style="animation-delay:${delay}s;">
        <div class="pmark">wydm<i></i></div>
        <div class="pqr">${miniQR()}</div>
        <div style="position:absolute;left:0;right:0;bottom:0;height:42px;background:linear-gradient(to top,rgba(0,0,0,0.65),transparent);"></div>
        <div class="pn"><span class="dotg"></span>${name}</div>
      </div><div class="tail"></div></div>`;
    const mapEl = root.querySelector('#a-map');
    if (mapEl) {
      // Static markup only — every value below is a literal from the design.
      mapEl.innerHTML = mapSVG()
        + pin('Rowan', 2, 26, 46, 3, -0.2) + pin('Quinn', 6, 58, 34, 4, -1.4) + pin('Avery', 3, 22, 72, 3, -2.6)
        + pin('Reese', 4, 80, 60, 2, -0.8) + pin('Casey', 1, 68, 84, 4, -2.0)
        + '<div class="radar"></div><div class="radar" style="animation-delay:1.6s"></div>'
        + '<div style="position:absolute;left:46%;top:54%;transform:translate(-50%,-50%);"><div style="width:40px;height:40px;border-radius:50%;background:rgba(232,106,76,0.18);display:flex;align-items:center;justify-content:center;"><div style="width:16px;height:16px;border-radius:50%;background:var(--coral);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div></div></div>';
    }

    /* scroll reveals. Timers guarantee the final visible state even when
       transitions are throttled (capture tools). */
    const finalizeShown = (el: Element) => {
      el.querySelectorAll<HTMLElement>('.bub, .note, .step, .typing').forEach((n) => { n.style.transition = 'none'; n.style.opacity = '1'; });
      el.querySelectorAll<HTMLElement>('.bub, .typing').forEach((n) => { n.style.transform = 'none'; });
      el.querySelectorAll<HTMLElement>('.pin').forEach((n) => { n.style.opacity = '1'; n.style.scale = '1'; });
    };
    const reveal = (sel: string) => {
      const el = root.querySelector(sel); if (!el) return;
      const fire = () => { el.classList.add('go'); timers.push(setTimeout(() => finalizeShown(el), 2000)); };
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((e) => { if (e.isIntersecting) { fire(); obs.disconnect(); } });
      }, { threshold: 0.2 });
      io.observe(el);
      observers.push(io);
    };
    reveal('.steps');
    reveal('#chat');
    reveal('#a-map');

    /* safety net: force final state even if nothing scrolls */
    timers.push(setTimeout(() => {
      ['.steps', '#chat', '#a-map'].forEach((s) => {
        const el = root.querySelector(s); if (el) { el.classList.add('go'); finalizeShown(el); }
      });
    }, 3000));

    return () => {
      observers.forEach((o) => o.disconnect());
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  // The #spread social-graph animation (the design's second script),
  // shared with /agents.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    return mountSpreadGraph(root, HUMAN_VARIANT);
  }, []);

  return (
    <div className="lp" ref={rootRef}>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 56px 88px' }}>
        <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 48, alignItems: 'center', maxWidth: 1320, margin: '0 auto' }}>
          <div>
            <h1 className="serif" style={{ fontSize: 78, fontWeight: 600, letterSpacing: -2.4, lineHeight: 1.0, marginTop: 18, maxWidth: 600 }}>
              Your next date is <span className="i" style={{ color: 'var(--coral)' }}>a few friends away</span>.
            </h1>
            <p style={{ fontSize: 21, color: 'var(--ink-2)', lineHeight: 1.55, marginTop: 24, maxWidth: 530, textWrap: 'pretty' as const }}>
              WYDM turns your profile into a card you post like anything else. It travels through your story, your group chats, your friends' reposts — and comes back as people who already have a reason to say yes. No swiping through strangers. No algorithm deciding who gets to see you.
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 34, alignItems: 'center', flexWrap: 'wrap' }}>
              <BuildMyCard />
            </div>
            <StoreLinkBadges />
          </div>

          {/* hero card stack */}
          <div className="hero-media" style={{ position: 'relative', height: 560 }}>
            <div className="pcard photo-gradient-3" style={{ position: 'absolute', width: 248, aspectRatio: '4 / 5', top: 30, left: 0, transform: 'rotate(-7deg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="scrim" />
              <div className="cm"><span className="w" style={{ fontSize: 20 }}>wydm</span><span className="dot" style={{ width: 5, height: 5, marginLeft: 2 }} /></div>
              <div className="meta"><div className="nm" style={{ fontSize: 30 }}>Noah, 26</div><div className="ask" style={{ fontSize: 16, marginTop: 6 }}>Would you date me?</div></div>
            </div>
            <div className="pcard photo-gradient-4" style={{ position: 'absolute', width: 248, aspectRatio: '4 / 5', top: 78, right: 0, transform: 'rotate(7deg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="qr" style={{ width: 54, height: 54 }} />
              <div className="scrim" />
              <div className="meta"><div className="nm" style={{ fontSize: 30 }}>Mia, 24</div><div className="ask" style={{ fontSize: 16, marginTop: 6 }}>Would you date me?</div></div>
            </div>
            {/* center card */}
            <div className="pcard photo-gradient-1" style={{ position: 'absolute', width: 300, aspectRatio: '4 / 5', top: 0, left: '50%', transform: 'translateX(-50%) rotate(-1deg)', zIndex: 3 }}>
              <div className="cm"><span className="w" style={{ fontSize: 26 }}>wydm</span><span className="dot" style={{ width: 6, height: 6, marginLeft: 3 }} /></div>
              <div className="qr" style={{ width: 72, height: 72, top: 18, right: 18, padding: 7, borderRadius: 13 }} />
              <div className="serif" style={{ position: 'absolute', right: -10, bottom: -70, fontStyle: 'italic', fontWeight: 600, fontSize: 280, lineHeight: 1, color: 'rgba(255,255,255,0.2)' }}>S</div>
              <div className="scrim" style={{ height: '62%' }} />
              <div className="meta" style={{ left: 24, bottom: 24 }}>
                <div className="nm" style={{ fontSize: 46 }}>Sam, 28</div>
                <div className="ask" style={{ fontSize: 24, marginTop: 10 }}>Would you date me?</div>
                <div className="url" style={{ fontSize: 13, marginTop: 14 }}><Lock size={13} /> wydm.app/p/sam</div>
              </div>
            </div>
            <ShareWires suffix="L" />
            <ShareBadges />
            {/* floating note */}
            <div style={{ position: 'absolute', bottom: 18, right: -8, width: 270, background: 'var(--surface)', borderRadius: 18, padding: 16, boxShadow: 'var(--shadow-lift)', transform: 'rotate(3deg)', zIndex: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--coral)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Heart size={13} /></div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink-3)' }}>Would date · via Jamie's story</div>
              </div>
              <div className="serif" style={{ fontSize: 15, lineHeight: 1.42, textWrap: 'pretty' as const }}>"Jamie reposted you. I've wanted an excuse to ask for a year."</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SPREAD: how the card travels ─────────────────────── */}
      <section id="spread" style={{ padding: '20px 56px 92px', overflow: 'hidden' }}>
        <div className="wrap" style={{ paddingLeft: 0, paddingRight: 0, textAlign: 'center' }}>
          <h2 className="serif" style={{ fontSize: 54, fontWeight: 600, letterSpacing: -1.4, lineHeight: 1.04, marginTop: 14, textWrap: 'balance' as const, maxWidth: 820, marginInline: 'auto' }}>
            One post. Two reposts. <span className="i" style={{ color: 'var(--coral)' }}>A date on Thursday.</span>
          </h2>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <div className="gchip">
            <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--coral)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Send size={12} /></span>
            <span id="gchipTxt">You posted your card to your story</span>
          </div>
        </div>
        <div className="graph-outer" id="graphOuter" style={{ marginTop: 16 }}>
          <div className="graph" id="graph">
            <svg className="gedges" id="gedges" viewBox="0 0 1100 560" />
            <div className="gcenter">
              <div className="pcard photo-gradient-1" style={{ width: 'var(--ccw, 150px)', aspectRatio: '4 / 5', boxShadow: '0 26px 50px -14px rgba(43,29,20,.5)' }}>
                <div className="cm"><span className="w" style={{ fontSize: 14 }}>wydm</span><span className="dot" style={{ width: 3.5, height: 3.5, marginLeft: 2 }} /></div>
                <div className="qr" style={{ width: 32, height: 32, top: 9, right: 9, padding: 3, borderRadius: 7 }} />
                <div className="scrim" style={{ height: '56%' }} />
                <div className="meta" style={{ left: 12, bottom: 11 }}>
                  <div className="nm" style={{ fontSize: 20 }}>Sam, 28</div>
                  <div className="ask" style={{ fontSize: 11, marginTop: 4 }}>Would you date me?</div>
                </div>
              </div>
            </div>
            <div className="gburst" />
            <div className="gpayoff">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div className="photo-gradient-1" style={{ flex: '0 0 auto', width: 52, height: 52, borderRadius: '50%', border: '3px solid var(--surface)', boxShadow: '0 8px 16px -6px rgba(43,29,20,.4)' }} />
                <div style={{ flex: '0 0 auto', width: 30, height: 30, borderRadius: '50%', background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 -20px', zIndex: 2, boxShadow: '0 6px 14px rgba(232,106,76,.5)' }}><Heart size={15} /></div>
                <div className="photo-gradient-4" style={{ flex: '0 0 auto', width: 52, height: 52, borderRadius: '50%', border: '3px solid var(--surface)', boxShadow: '0 8px 16px -6px rgba(43,29,20,.4)' }} />
                <div style={{ textAlign: 'left', marginLeft: 6 }}>
                  <div className="serif" style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.05 }}>Remy said yes.</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 6 }}>Jamie's flatmate · 2 shares away</div>
                </div>
              </div>
              <div style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.5, marginTop: 16, textAlign: 'left', textWrap: 'pretty' as const }}>"Jamie put your card in our group chat. Coffee Thursday?"</div>
              <Link to={SIGNUP} className="btn btn-coral" style={{ width: '100%', marginTop: 16 }}>Get a card of your own</Link>
            </div>
          </div>
        </div>
        <div className="gstats">
          <div className="gstat"><div className="n"><span id="s-seen">0</span></div><div className="l">Saw your card</div></div>
          <div className="gstat"><div className="n"><span id="s-yes">0</span></div><div className="l">Said would date</div></div>
          <div className="gstat"><div className="n"><span id="s-date">0</span></div><div className="l">Date on Thursday</div></div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <section style={{ padding: '84px 56px', background: 'var(--bg-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div className="wrap" style={{ paddingLeft: 0, paddingRight: 0, maxWidth: 1240 }}>
          <h2 className="serif" style={{ fontSize: 52, fontWeight: 600, letterSpacing: -1.4, lineHeight: 1.04, marginTop: 14, maxWidth: 820, textWrap: 'balance' as const }}>
            Post it once. <span className="i" style={{ color: 'var(--coral)' }}>Your circle does the rest.</span>
          </h2>
          <div className="steps" style={{ marginTop: 52 }}>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-3" style={{ left: 8, top: 16, transform: 'rotate(-11deg)' }} />
                <div className="mcard photo-gradient-1" style={{ left: 30, top: 6, transform: 'rotate(-1deg)', zIndex: 2 }}>
                  <span className="w2">wydm<i /></span><span className="s2" /><span className="n2">You</span>
                </div>
                <div className="act" style={{ width: 32, height: 32, left: 76, top: 58, zIndex: 3 }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </div>
              </div>
              <div className="st">Make your card</div>
              <div className="sb">Photos, a bio, the song you won't shut up about. Ninety seconds.</div>
            </div>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-4" style={{ left: 16, top: 10, transform: 'rotate(-6deg)', zIndex: 2 }}>
                  <span className="w2">wydm<i /></span>
                  <span className="q2"><MiniQR /></span>
                  <span className="s2" /><span className="n2">Mia</span>
                </div>
                <div className="act" style={{ width: 32, height: 32, left: 74, top: 26, zIndex: 3 }}>
                  <Send size={15} />
                </div>
              </div>
              <div className="st">Put it in your story</div>
              <div className="sb">Friends repost it, their friends screenshot it, and it lands in circles you'd never reach.</div>
            </div>
            <div className="step">
              <div className="ill">
                <div className="mcard photo-gradient-5" style={{ left: 10, top: 16, transform: 'rotate(-10deg)' }} />
                <div className="mcard photo-gradient-2" style={{ left: 40, top: 8, transform: 'rotate(8deg)', zIndex: 2 }} />
                <div className="act" style={{ width: 38, height: 38, left: 38, top: 30, zIndex: 3 }}><Heart size={19} /></div>
              </div>
              <div className="st">Go on the date</div>
              <div className="sb">The yeses come back with names and faces attached. Message the one you like.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: built to go viral ───────────────────────── */}
      <section style={{ padding: '96px 56px' }}>
        <div className="feat wrap" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <div>
            <h2>Built to go <span className="i" style={{ color: 'var(--coral)' }}>viral</span>, not swiped past.</h2>
            <p>Your card is a link and a QR, so it survives every hop. Anyone it reaches can view and message you without downloading a thing.</p>
            <ul>
              <li><Tick /> A link that looks good in a bio: wydm.app/p/you</li>
              <li><Tick /> A QR that survives a screenshot of a screenshot</li>
              <li><Tick /> See who reshared you, and which share brought the yes</li>
            </ul>
          </div>
          <div className="feat-media media-scale" style={{ position: 'relative', height: 480 }}>
            <div className="pcard photo-gradient-1" style={{ position: 'absolute', width: 300, aspectRatio: '4 / 5', left: 60, top: 10, transform: 'rotate(-6deg)' }}>
              <div className="cm"><span className="w" style={{ fontSize: 26 }}>wydm</span><span className="dot" style={{ width: 6, height: 6, marginLeft: 3 }} /></div>
              <div className="serif" style={{ position: 'absolute', right: -6, bottom: -66, fontStyle: 'italic', fontWeight: 600, fontSize: 270, lineHeight: 1, color: 'rgba(255,255,255,0.2)' }}>S</div>
              <div className="scrim" style={{ height: '60%' }} />
              <div className="meta" style={{ left: 22, bottom: 22 }}>
                <div className="nm" style={{ fontSize: 42 }}>Sam, 28</div>
                <div className="ask" style={{ fontSize: 22, marginTop: 9 }}>Would you date me?</div>
                <div className="url" style={{ fontSize: 12, marginTop: 12 }}><Lock size={12} /> wydm.app/p/sam</div>
              </div>
            </div>
            {/* popped QR */}
            <div style={{ position: 'absolute', right: 40, top: 60, transform: 'rotate(6deg)', zIndex: 4 }}>
              <div className="qr" style={{ position: 'relative', width: 150, height: 150, padding: 14, borderRadius: 22, boxShadow: '0 30px 60px -16px rgba(43,29,20,0.4)' }} />
              <div style={{ position: 'absolute', left: '50%', bottom: -18, transform: 'translateX(-50%)', background: 'var(--ink)', color: '#FDF6EC', fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 13, padding: '8px 16px', borderRadius: 100, whiteSpace: 'nowrap' }}>Scan to open</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: nearby map ──────────────────────────────── */}
      <section style={{ padding: '96px 56px', background: 'var(--bg-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div className="feat flip wrap" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <div>
            <h2>Discover people <span className="i" style={{ color: 'var(--coral)' }}>near you</span>.</h2>
            <p>Every pin is someone nearby with a live card. Open one and message them directly.</p>
            <ul>
              <li><Tick /> Map view + swipeable deck</li>
              <li><Tick /> Set your own radius</li>
            </ul>
          </div>
          <div className="feat-media">
            <div className="mapbox" id="a-map" />
          </div>
        </div>
      </section>

      {/* ── FEATURE: message anyone (dark band) ──────────────── */}
      <section style={{ padding: '96px 56px', background: 'var(--ink)', color: 'var(--bg)' }}>
        <div className="feat wrap" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <div>
            <h2 style={{ color: 'var(--bg)' }}>No matching. <span className="i" style={{ color: 'var(--coral)' }}>Just say hi.</span></h2>
            <p style={{ color: 'rgba(246,237,223,0.72)' }}>See someone you like, send the first message. No matching, no waiting for permission.</p>
            <BuildMyCard style={{ marginTop: 28 }} />
          </div>
          <div className="feat-media chat" id="chat">
            <div className="bub in">jamie put your card in our group chat 😂</div>
            <div className="bub out">jamie is getting a christmas present this year<span className="t">9:24 · read</span></div>
            <div className="bub in">okay 94% said yes is kind of a flex</div>
            <div className="bub out">so… would you date me?<span className="t">9:27 · read</span></div>
            <div className="typing"><span /><span /><span /></div>
          </div>
        </div>
      </section>

      {/* ── FEATURE: keepers ─────────────────────────────────── */}
      <section style={{ padding: '96px 56px', background: 'var(--bg-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
        <div className="feat flip wrap" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <div>
            <h2>Keep the ones you <span className="i" style={{ color: 'var(--coral)' }}>like</span>.</h2>
            <p>Bookmark the cards worth coming back to.</p>
          </div>
          <div className="feat-media media-scale" style={{ position: 'relative', height: 470, justifySelf: 'start', width: '100%' }}>
            <div className="pcard kfloat photo-gradient-6" style={{ position: 'absolute', width: 216, aspectRatio: '3 / 4', left: 44, top: 50, transform: 'rotate(-9deg)', boxShadow: 'var(--shadow-card)' }}>
              <div className="cm"><span className="w" style={{ fontSize: 15 }}>wydm</span><span className="dot" style={{ width: 4, height: 4, marginLeft: 2 }} /></div>
              <div className="qr" style={{ width: 36, height: 36, top: 12, right: 12, padding: 4, borderRadius: 8 }} />
              <div className="scrim" style={{ height: '44%' }} />
              <div className="meta" style={{ left: 14, bottom: 13 }}><div className="nm" style={{ fontSize: 20 }}>Riley</div></div>
            </div>
            <div className="pcard kfloat photo-gradient-4" style={{ position: 'absolute', width: 216, aspectRatio: '3 / 4', left: 158, top: 26, transform: 'rotate(7deg)', boxShadow: 'var(--shadow-card)', animationDelay: '.9s' }}>
              <div className="cm"><span className="w" style={{ fontSize: 15 }}>wydm</span><span className="dot" style={{ width: 4, height: 4, marginLeft: 2 }} /></div>
              <div className="qr" style={{ width: 36, height: 36, top: 12, right: 12, padding: 4, borderRadius: 8 }} />
              <div className="scrim" style={{ height: '44%' }} />
              <div className="meta" style={{ left: 14, bottom: 13 }}><div className="nm" style={{ fontSize: 20 }}>Dani</div></div>
            </div>
            <div className="pcard kfloat photo-gradient-2" style={{ position: 'absolute', width: 236, aspectRatio: '3 / 4', left: 98, top: 98, transform: 'rotate(-2deg)', zIndex: 3, animationDelay: '1.8s' }}>
              <div className="cm"><span className="w" style={{ fontSize: 16 }}>wydm</span><span className="dot" style={{ width: 4, height: 4, marginLeft: 2 }} /></div>
              <div className="qr" style={{ width: 40, height: 40, top: 13, right: 13, padding: 4, borderRadius: 9 }} />
              <div className="scrim" style={{ height: '50%' }} />
              <div className="meta" style={{ left: 16, bottom: 16 }}><div className="nm" style={{ fontSize: 24 }}>Alex</div><div className="url" style={{ fontSize: 11, marginTop: 6 }}>wydm.app/p/alex</div></div>
            </div>
            {/* bookmark badge on top of the whole graphic */}
            <div style={{ position: 'absolute', top: 0, right: 34, zIndex: 10, width: 56, height: 56, borderRadius: 17, background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 30px -6px rgba(232,106,76,0.6)', transform: 'rotate(6deg)' }}>
              <svg width={25} height={25} viewBox="0 0 24 24" fill="#fff"><path d="M6 3h12a1 1 0 011 1v17l-7-4-7 4V4a1 1 0 011-1z" /></svg>
            </div>
            {/* saved pill */}
            <div style={{ position: 'absolute', left: 26, bottom: 10, zIndex: 6, display: 'inline-flex', alignItems: 'center', gap: 11, background: 'var(--surface)', borderRadius: 100, padding: '11px 20px 11px 12px', boxShadow: 'var(--shadow-lift)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width={15} height={15} viewBox="0 0 24 24" fill="#fff"><path d="M6 3h12a1 1 0 011 1v17l-7-4-7 4V4a1 1 0 011-1z" /></svg>
              </div>
              <div><div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18, lineHeight: 1 }}>Keepers</div><div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>12 saved</div></div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
